import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Text, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";
import Animated, {
  Easing,
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Svg, { Circle } from "react-native-svg";

type StepperStep = Readonly<{
  title: string;
  description: string;
}>;

type StepperProps = Readonly<{
  currentStep: number;
  steps: StepperStep[];
}>;

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const CIRCLE_RADIUS = 18;
const STROKE_WIDTH = 5;
const CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const ScStepper = ({ currentStep, steps }: StepperProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const totalSteps = steps.length;
  const activeStep = steps[currentStep - 1];

  const targetOffset =
    CIRCUMFERENCE - (CIRCUMFERENCE * currentStep) / totalSteps;
  const strokeOffset = useSharedValue(CIRCUMFERENCE);

  useEffect(() => {
    strokeOffset.value = withTiming(targetOffset, {
      duration: 500,
      easing: Easing.out(Easing.cubic),
    });
  }, [currentStep]);

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: strokeOffset.value,
  }));

  return (
    <View className="flex-row items-center gap-3">
      <View className="justify-center items-center">
        <Svg width={50} height={50}>
          <Circle
            stroke={themeVars["--color-ash-default"] as string}
            fill="none"
            cx="25"
            cy="25"
            r={CIRCLE_RADIUS}
            strokeWidth={STROKE_WIDTH}
          />
          <AnimatedCircle
            stroke={themeVars["--color-primary-default"] as string}
            fill="none"
            cx="25"
            cy="25"
            r={CIRCLE_RADIUS}
            strokeWidth={STROKE_WIDTH}
            strokeDasharray={CIRCUMFERENCE}
            strokeLinecap="round"
            transform="rotate(-90 25 25)"
            animatedProps={animatedProps}
          />
        </Svg>
        <View className="absolute">
          <Text className="text-xs font-semibold text-onyx">
            {currentStep}/{totalSteps}
          </Text>
        </View>
      </View>

      <View>
        <Text className="font-semibold hidden sm:flex text-onyx">
          {t(activeStep.title)}
        </Text>
        <Text className="text-slate text-xs hidden sm:flex text-ash-3">
          {t(activeStep.description)}
        </Text>
      </View>
    </View>
  );
};

export default ScStepper;
